import React from "react";

import { Area, CartesianGrid, ComposedChart, Line, Tooltip as RechartsTooltip, ReferenceDot, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from "recharts";

import { useSoilSightCurrency, useTranslation } from "utils";

import { ONBOARDING_CHART_COLORS, ONBOARDING_CHART_HEIGHT } from "consts";

import { Typography } from "components";

import {
  OnboardingForecastPoint,
  getForecastYAxisTicks,
  getForecastYDomain,
  getForecastYearTicks,
} from "pages/app/scenario-onboarding/utils/onboardingForecast";

type OnboardingForecastChartProps = {
  data: OnboardingForecastPoint[];
  /** "dashed" for the potential preview, "solid" for a created scenario, "none" for baseline-only. */
  changeLineStyle: "dashed" | "solid" | "none";
  /** "behind" tints the gap as a shortfall, the way the acquisition chart shades its delta. */
  changeTone?: "ahead" | "behind";
  /** Hover pill content for the hovered year (return null to hide) — the gap differs per year. */
  getTooltipContent?: (point: OnboardingForecastPoint) => string | null;
};

// Recharts injects active/payload into the content element at render time.
type CalloutTooltipProps = {
  active?: boolean;
  payload?: Array<{ payload?: OnboardingForecastPoint }>;
  getTooltipContent?: (point: OnboardingForecastPoint) => string | null;
};

const CalloutTooltip = ({ active, payload, getTooltipContent }: CalloutTooltipProps) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;

  const label = getTooltipContent?.(point);
  if (!label) return null;

  return (
    <div className="onboarding-forecast-chart__tooltip" data-testid="onboarding-forecast-tooltip">
      <Typography variant="paragraph-m">{label}</Typography>
    </div>
  );
};

export const OnboardingForecastChart = ({ data, changeLineStyle, changeTone = "ahead", getTooltipContent }: OnboardingForecastChartProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { formatCurrency } = useSoilSightCurrency();

  if (data.length === 0) return null;

  const lastPoint = data[data.length - 1];
  const showChangeLine = changeLineStyle !== "none";
  const xTicks = getForecastYearTicks(data.length, [1, 3, 5, 8, 10]);
  const yTicks = getForecastYAxisTicks(data);
  const yDomain = getForecastYDomain(data, yTicks);

  return (
    <div className="onboarding-forecast-chart" data-testid="onboarding-forecast-chart">
      <ResponsiveContainer width="100%" height={ONBOARDING_CHART_HEIGHT}>
        <ComposedChart data={data} margin={{ top: 32, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="4 4" vertical={false} />
          {/* Right padding insets the last data point while the gridlines keep running to the
              content edge (Figma); the plot itself spans the full card width. */}
          <XAxis
            dataKey="yearNum"
            ticks={xTicks}
            tickFormatter={(yearNum: number) => t("onboarding.chart.yearTick", { year: yearNum })}
            tickLine={false}
            axisLine={false}
            padding={{ right: 48 }}
          />
          {/* Zero-based domain with round, data-fitted tick labels (no "0" label at the origin). */}
          <YAxis
            domain={yDomain}
            ticks={yTicks}
            tickFormatter={(value: number) => formatCurrency(value, { decimals: 0 })}
            tickLine={false}
            axisLine={false}
            width={72}
          />

          {getTooltipContent && (
            <RechartsTooltip
              content={<CalloutTooltip getTooltipContent={getTooltipContent} />}
              cursor={{ stroke: ONBOARDING_CHART_COLORS.guide, strokeWidth: 1 }}
            />
          )}

          {showChangeLine && (
            <Area
              type="monotone"
              dataKey="band"
              stroke="none"
              fill={changeTone === "behind" ? ONBOARDING_CHART_COLORS.behindBand : ONBOARDING_CHART_COLORS.band}
              isAnimationActive={false}
              activeDot={false}
            />
          )}

          <Line type="monotone" dataKey="baselineCumulative" stroke={ONBOARDING_CHART_COLORS.baseline} strokeWidth={2} dot={false} isAnimationActive={false} />
          {showChangeLine && (
            <Line
              type="monotone"
              dataKey="changeCumulative"
              stroke={ONBOARDING_CHART_COLORS.change}
              strokeWidth={2}
              strokeDasharray={changeLineStyle === "dashed" ? "6 6" : undefined}
              dot={false}
              isAnimationActive={false}
            />
          )}

          <ReferenceLine x={lastPoint.yearNum} stroke={ONBOARDING_CHART_COLORS.guide} strokeWidth={1} />
          <ReferenceDot
            x={lastPoint.yearNum}
            y={lastPoint.baselineCumulative}
            r={5}
            fill={ONBOARDING_CHART_COLORS.baseline}
            stroke={ONBOARDING_CHART_COLORS.dotOutline}
            strokeWidth={1}
          />
          {showChangeLine && lastPoint.changeCumulative != null && (
            <ReferenceDot
              x={lastPoint.yearNum}
              y={lastPoint.changeCumulative}
              r={5}
              fill={ONBOARDING_CHART_COLORS.change}
              stroke={ONBOARDING_CHART_COLORS.dotOutline}
              strokeWidth={1}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
